import { useState } from 'react';

import { useSignIn } from '@clerk/clerk-react';
import { ArrowLeft } from 'lucide-react';

import type { FormEvent } from 'react';

import { ErrorAlert } from '~/components/common/ErrorAlert';
import { Alert } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { Field, FieldLabel } from '~/components/ui/field';
import { Input } from '~/components/ui/input';
import { Spinner } from '~/components/ui/spinner';
import { useAsyncForm } from '~/hooks/useAsyncForm';
import { extractClerkError } from '~/utils/apiErrors';

interface MagicLinkFormProps {
  onBackToOAuth: () => void;
  /** Preserves the intended post-sign-in destination through the magic-link round trip. */
  redirectUrl?: string | null;
}

// Thrown when Clerk returns a non-error status that still means no link was sent.
const UNEXPECTED_STATUS_MESSAGE = 'Unable to send magic link. Please try again.';

export function MagicLinkForm({ onBackToOAuth, redirectUrl }: MagicLinkFormProps) {
  const { signIn, isLoaded } = useSignIn();
  const [email, setEmail] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const {
    submit,
    isSubmitting: isLoading,
    error,
    clearError,
  } = useAsyncForm(
    async () => {
      if (!signIn) {
        throw new Error(UNEXPECTED_STATUS_MESSAGE);
      }

      // Clerk SDK integration pattern
      // Create email link sign-in with redirect URL, forwarding the intended
      // post-sign-in destination so it survives the magic-link round trip.
      const callbackUrl = new URL('/magic-link-callback', window.location.origin);
      if (redirectUrl) {
        callbackUrl.searchParams.set('redirect_url', redirectUrl);
      }

      const result = await signIn.create({
        strategy: 'email_link',
        identifier: email,
        redirectUrl: callbackUrl.toString(),
      });

      // A non-first-factor status means the link was not dispatched.
      if (result.status !== 'needs_first_factor') {
        throw new Error(UNEXPECTED_STATUS_MESSAGE);
      }
    },
    {
      onSuccess: () => setIsSuccess(true),
      mapError: (err) =>
        err instanceof Error && err.message === UNEXPECTED_STATUS_MESSAGE
          ? UNEXPECTED_STATUS_MESSAGE
          : extractClerkError(err, 'Failed to send magic link'),
    }
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!signIn || !isLoaded) return;

    await submit();
  };

  const handleRetry = () => {
    clearError();
    setIsSuccess(false);
  };

  // Per spec: Success state replaces form with confirmation message and back button
  if (isSuccess) {
    return (
      <div className="flex flex-col gap-6 text-center">
        {/* Informational, not an error - no alert role */}
        <Alert variant="success">
          <div>
            <p className="text-base font-medium">Check your email for sign-in link</p>
            <p className="mt-2 text-sm">
              We sent a magic link to <strong>{email}</strong>. The link expires in 15 minutes -
              check your spam folder if it doesn't arrive shortly.
            </p>
          </div>
        </Alert>

        <Button variant="outline" size="lg" className="w-full" onClick={onBackToOAuth}>
          <ArrowLeft data-icon="inline-start" />
          Back to Sign In
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-6">
      {/* Email Field */}
      <Field>
        <FieldLabel htmlFor="magic-link-email">Email</FieldLabel>
        <Input
          id="magic-link-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
          autoComplete="email"
          // oxlint-disable-next-line jsx-a11y/no-autofocus -- this is the sole field of a standalone sign-in form; focusing it on mount matches expected behavior for a fresh auth screen
          autoFocus
        />
      </Field>

      {/* Error Alert - Per spec: Display error with retry callback */}
      {error && <ErrorAlert title="Sign-In Error" message={error} onRetry={handleRetry} />}

      {/* Submit Button - loading state pattern */}
      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={isLoading || !isLoaded}
        aria-busy={isLoading}
      >
        {isLoading && (
          <Spinner
            data-icon="inline-start"
            data-testid="magic-link-submit-spinner"
            aria-hidden="true"
          />
        )}
        Send Magic Link
      </Button>

      {/* Back Button - Per spec: Call onBackToOAuth() prop */}
      <Button
        type="button"
        variant="ghost"
        size="lg"
        className="w-full"
        onClick={onBackToOAuth}
        disabled={isLoading}
      >
        <ArrowLeft data-icon="inline-start" />
        Other sign-in options
      </Button>
    </form>
  );
}
